import { Component, OnInit } from '@angular/core';
import { FormButton } from '../../../core/components/form-field-button/form-field-button.component';
import { Item } from '../../../core/components/listbox/listbox.component';
import { DataTaskService } from '../../../core/services/data-task.service';
import { ModalService } from '../../../core/services/modal.service';
import { StateService } from '../../../core/services/state.service';
import { EconomyService, FactoringItem, PaymentTypeItem } from '../../../services/economy.service';
import { DepartmentItem, LogisticService } from '../../../services/logistic.service';
import { SearchProjectItem, TypeaheadService } from '../../../services/typeahead.service';
import { Grid } from '../../../core/components/grid/grid.model';
import { GridOptions } from '../../../core/components/grid/grid.types';
import { ToastService } from '@app/core/services/toast.service';

@Component({
    selector: 'tt-invoice-head',
    templateUrl: './invoice-head.component.html',
    styleUrls: ['./invoice-head.component.css'],
    standalone: false,
})
export class InvoiceHeadComponent implements OnInit {
    invoiceNo;

    public webPageName: string;

    public selectedBgMainView: string = 'INVOICELINE';

    public selectedBgTerms: string = 'ADDRESS';

    public selectedBgDelivery: string = 'ADRSTAT';

    public statCustNoButtons: FormButton[] = [
        { id: 'edit', type: 'primary', icon: 'fas fa-edit', goto: 'customeredit', gotoParms: () => this.edit!.stat_cust_no ?? '' },
        { id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'customersmenu', gotoParms: () => this.edit!.stat_cust_no ?? '' },
    ];

    public deliveryCustNoButtons: FormButton[] = [
        { id: 'edit', type: 'primary', icon: 'fas fa-edit', goto: 'customeredit', gotoParms: () => this.edit!.delivery_cust_no ?? '' },
        { id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'customersmenu', gotoParms: () => this.edit!.delivery_cust_no ?? '' },
    ];

    public invoiceCustNoButtons: FormButton[] = [
        { id: 'edit', type: 'primary', icon: 'fas fa-edit', goto: 'customeredit', gotoParms: () => this.edit!.invoice_cust_no ?? '' },
        { id: 'goto', type: 'primary', icon: 'fas fa-chevron-right', goto: 'customersmenu', gotoParms: () => this.edit!.invoice_cust_no ?? '' },
    ];

    public edit?: Edit;

    public selectListPayments: PaymentTypeItem[] = [];

    public selectListFactorings: FactoringItem[] = [];

    public selectListDepartments: DepartmentItem[] = [];

    public itemsListInvoiceLines: Item[] = [];

    public grid?: GridOptions;

    public printingOk = false;

    public sendingInvoice = false;

    public saving = false;

    constructor(
        private stateService: StateService,
        private modal: ModalService,
        private logistic: LogisticService,
        private economy: EconomyService,
        private datatask: DataTaskService,
        private typeahead: TypeaheadService,
        private toast: ToastService,
    ) {
        this.invoiceNo = this.stateService.getStateParams()['invoice_no'];
        this.webPageName = this.stateService.getCurrentName();
    }

    private async loadEdit() {
        if (!!this.invoiceNo) {
            this.edit = (await this.datatask.Post(751, { invoice_no: this.invoiceNo }))[0];
        }
    }

    private async loadPayments() {
        this.selectListPayments = await this.economy.getPaymentTypes();
    }

    private async loadFactorings() {
        this.selectListFactorings = await this.economy.getFactorings();
    }

    private async loadDepartments() {
        if (!this.edit) return;

        return (this.selectListDepartments = await this.logistic.getDepartments(this.edit.businessco_no));
    }

    private async loadInvoiceLines() {
        if (!this.edit) return;

        return (this.itemsListInvoiceLines = await this.datatask.Post(752, { invoice_no: this.edit.invoice_no }));
    }

    private loadGrid() {
        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId('invoiceline_grid')
                .setLoadDataFunction({
                    method: 3784,
                    parameters: { invoice_no: this.edit!.invoice_no },
                })
                .setKendoConfig({ height: '100%' })
                .setToolbar({ wrapping: true });
        } else {
            this.grid?.gridfunc?.read();
        }
    }

    public searchProjectNames = () => {
        return this.typeahead.searchProjects({
            project_name: this.edit!.project_name,
            businessco_no: this.edit!.businessco_no,
        });
    };

    public onProjectNameSelected(event?: SearchProjectItem) {
        if (!this.edit) return;

        this.edit.project_keyno = !!event ? '' + event.item_id : '0';
        this.edit.project_name = event?.item_name ?? '';
    }

    public async setPrintedOk() {
        if (!!this.edit) {
            try {
                this.printingOk = true;

                await this.datatask.Post(828, { invoice_no: this.edit.invoice_no });

                this.stateService.back();
            } finally {
                this.printingOk = false;
            }
        }
    }

    public async sendChanges() {
        if (!!this.edit) {
            try {
                this.sendingInvoice = true;

                const response = (await this.datatask.Post(2284, { invoice_no: this.edit.invoice_no }))[0];

                if (response?.errorcode !== '0') {
                    this.modal.show({
                        type: 'warning',
                        title: 'error',
                        translateTitle: true,
                        message: response.errormessage,
                    });
                } else {
                    this.stateService.back();
                }
            } finally {
                this.sendingInvoice = false;
            }
        }
    }

    public async saveChanges() {
        if (!!this.edit) {
            try {
                this.saving = true;

                const response = (await this.datatask.Post(2306, this.edit))[0];

                if (response?.errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
                } else {
                    await this.loadEdit();
                    this.toast.addToast({ id: `invoicehead.${this.edit.invoice_no}.save.${Date.now()}`, type: 'success', title: 'saved!', message: 'changes_have_been_saved', translateMessage: true, translateTitle: true });
                }
            } finally {
                this.saving = false;
            }
        }
    }

    private async init() {
        await this.loadEdit();
        this.loadPayments();
        this.loadFactorings();
        this.loadDepartments();
        this.loadInvoiceLines();
        this.loadGrid();
    }

    ngOnInit() {
        this.init();
    }
}

interface Edit {
    invoice_no: string;
    invoice_no2: string;
    invoice_date: string;
    businessco_no: string;
    businessco_companyname: string;
    payment_date: string;
    stat_cust_no: string;
    stat_cust_name: string;
    stat_cust_address: string;
    delivery_cust_no: string;
    delivery_cust_name: string;
    delivery_cust_address: string;
    invoice_cust_no: string;
    invoice_cust_name: string;
    invoice_cust_address: string;
    invoice_valuta_id: string;
    payment_id: string;
    factoring_no: string;
    printed_ok: string;
    show_ehf: string;
    show_documents: string;
    nbr_of_documents: string;
    customers_ref1: string;
    customers_ref2: string;
    customers_orderno: string;
    show_tracesearch: string;
    project_keyno: string;
    project_name: string;
    department_id: string;
    reg_by_user_name: string;
    errormessage: string;
}
